import { useState } from 'react';

import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/Dialog';
import DisabledButtonTooltip from '@/components/DisabledButtonTooltip';
import { AMEX_SETTLEMENT_DAYS, invoiceDelayHours } from '@/config';
import Info from '@/icons/circle-info.svg';
import Download from '@/icons/download.svg';
import Refresh from '@/icons/refresh.svg';
import invoiceModel from '@/models/invoice';
import { PAYMENT_STATUS, PAYMENT_TYPE } from '@/models/payment';
import { TAX_INFO_TAB } from '@/pages/Account/constants';
import routes from '@/routes';
import { setCanRegenerateInvoice } from '@/store/billing';
import { setCurrentTab } from '@/store/profile';
import {
	cn,
	formatDashDate,
	formatDate,
	showError,
	showSuccess
} from '@/utils';
import { formatPrice } from '@/utils/money';

import { useGetPaymentReference } from './hooks';

export function DateCell({ row }) {
	return (
		<div className="flex items-center">
			<span className="text-xs leading-5 text-grey-500">
				{row.original.createdAt ? formatDate(row.original.createdAt) : '-'}
			</span>
		</div>
	);
}

export function ReferenceCell({ row }) {
	const getPaymentReference = useGetPaymentReference();

	return (
		<div className="flex items-center">
			<span className="text-xs leading-5 text-grey-500">
				{getPaymentReference(row.original)}
			</span>
		</div>
	);
}

export function BillingRangeCell({ row }) {
	const { t } = useTranslation();
	return (
		<div className="flex items-center">
			<span className="text-xs leading-5 text-grey-500">
				{row.original.periodFrom && row.original.periodTo
					? t('billing.subscription.table.billingRange.description', {
							range1: formatDashDate(row.original.periodFrom),
							range2: formatDashDate(row.original.periodTo)
						})
					: '-'}
			</span>
		</div>
	);
}

export function ValueCell({ row }) {
	let { currency } = row.original;

	if (
		row.original.type === PAYMENT_TYPE.DISPUTE &&
		row.original.disputeCurrency
	) {
		currency = row.original.disputeCurrency;
	}

	return (
		<div className="flex items-center">
			<span className="text-xs font-semibold leading-5">
				{formatPrice({
					amount: row.original.amount,
					currency
				})}
			</span>
		</div>
	);
}

export function ButtonsCell({ row, variant = 'ghost', mobile = false }) {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const [fileDownloading, setFileDownloading] = useState(false);
	const [isRegeneratingInvoice, setIsRegeneratingInvoice] = useState(false);
	const taxInformation = useSelector(
		state => state.profile.user?.taxInformation || {}
	);
	const [completeTaxInfoModal, setCompleteTaxInfoModal] = useState(false);

	const isTaxInfoCompleted = Object.keys(taxInformation).length > 0;

	const buttonDisabled = !row.original.invoiceId && isTaxInfoCompleted;

	const downloadRow = async () => {
		try {
			setFileDownloading(true);
			const { url } = await invoiceModel.getDownload(row.original.invoiceId);

			const response = await fetch(url);

			if (!response.ok) {
				throw new Error(response.statusText);
			}

			const fileName = `pdfe-invoice-${formatDashDate(row.original.periodFrom)}/${formatDashDate(row.original.periodTo)}`;

			saveAs(await response.blob(), `${fileName}.pdf`);

			showSuccess(t('myDocuments.toast.downloadDocumentSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setFileDownloading(false);
		}
	};

	const regenerateInvoice = async () => {
		try {
			setIsRegeneratingInvoice(true);
			await invoiceModel.regenerate(row.original.invoiceId);
			dispatch(setCanRegenerateInvoice({ rowId: row.id, value: false }));
			showSuccess(t('billing.toast.regenerateInvoiceSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setIsRegeneratingInvoice(false);
		}
	};

	const pendingInvoiceTitle = !row.original.settledAt
		? t('billing.invoiceInProcess.title')
		: t('billing.pendingInvoice');

	const pendingInvoiceDescription = !row.original.settledAt
		? t('billing.invoiceInProcess.description', {
				hours: invoiceDelayHours,
				amexSettlementDays: AMEX_SETTLEMENT_DAYS
			})
		: null;

	return (
		<div
			className={cn(
				'flex w-full items-center justify-end gap-4',
				mobile && 'flex-wrap justify-start gap-2'
			)}
		>
			{row.original.canRegenerateInvoice && row.original.invoiceId && (
				<Button
					className="h-6 py-[2px] pl-2 pr-3 text-xs text-black"
					onClick={regenerateInvoice}
					loading={isRegeneratingInvoice}
					startIcon={<Refresh aria-hidden />}
					variant="outline"
				>
					{t('billing.regenerateInvoice')}
				</Button>
			)}
			{row.original.status === PAYMENT_STATUS.PAID && (
				<DisabledButtonTooltip
					show={buttonDisabled}
					title={buttonDisabled && pendingInvoiceTitle}
					description={pendingInvoiceDescription}
				>
					<Button
						aria-label={t('common.download')}
						className={cn(mobile && 'h-6 py-[2px] pl-2 pr-3 text-xs')}
						onClick={() => {
							return row.original.invoiceId
								? downloadRow()
								: setCompleteTaxInfoModal(true);
						}}
						disabled={buttonDisabled}
						loading={fileDownloading}
						size={mobile ? undefined : 'iconMd'}
						variant={variant}
					>
						<Download aria-hidden style={{ width: 20, height: 20 }} />
						{mobile && (
							<span className="ml-2">{t('common.download')}</span>
						)}
					</Button>
				</DisabledButtonTooltip>
			)}
			<CompleteTaxInfoModal
				open={completeTaxInfoModal}
				onOpenChange={setCompleteTaxInfoModal}
			/>
		</div>
	);
}

export function CompleteTaxInfoModal({ open, onOpenChange }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const goToTaxInformation = () => {
		dispatch(setCurrentTab('taxInfo'));
		onOpenChange(false);
		navigate(routes.myAccount, {
			state: { currentTab: TAX_INFO_TAB }
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogTop
					title={t('billing.modals.completeTaxInfo.title')}
					description={t('billing.modals.completeTaxInfo.title')}
				/>

				<div className="flex flex-col items-center gap-2 px-4 py-9">
					<Info className="h-5 w-5 text-red" />
					<p className="whitespace-pre-line text-center text-sm text-grey-500">
						{t('billing.modals.completeTaxInfo.text')}
					</p>
				</div>
				<DialogFooter singleButton>
					<Button onClick={() => goToTaxInformation()} className="w-full">
						{t('billing.modals.completeTaxInfo.completeInfo')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
